import type { NodeType, WorkflowAssistantAction, WorkflowNode } from '@/stores/WorkflowStore/WorkflowStore'

export interface AssistantToolExecutionActivityLike {
    id: string
    name: string
    status: string
}

const SUPPORTED_NODE_TYPES = new Set<NodeType>([
    'input',
    'agent',
    'join',
    'pause',
    'base64',
    'excel',
    'ocr',
    'email',
    'knowledge_base',
    'ingestion',
])

const ACTION_PRIORITY: Record<WorkflowAssistantAction['action_type'], number> = {
    add_node: 0,
    update_node: 1,
    disconnect_nodes: 2,
    connect_nodes: 3,
    remove_node: 4,
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false
    }

    const prototype = Object.getPrototypeOf(value)
    return prototype === Object.prototype || prototype === null
}

export function cloneAssistantData<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.map(item => cloneAssistantData(item)) as T
    }

    if (isPlainRecord(value)) {
        const cloned: Record<string, unknown> = {}
        for (const [key, entry] of Object.entries(value)) {
            cloned[key] = cloneAssistantData(entry)
        }
        return cloned as T
    }

    return value
}

export function resolveAssistantToolArgs(
    toolArgs: unknown,
    rawArgs: string,
): Record<string, unknown> {
    if (isPlainRecord(toolArgs) && Object.keys(toolArgs).length > 0) {
        return cloneAssistantData(toolArgs)
    }

    if (!rawArgs) {
        return isPlainRecord(toolArgs) ? cloneAssistantData(toolArgs) : {}
    }

    try {
        const parsed = JSON.parse(rawArgs) as unknown
        if (isPlainRecord(parsed)) {
            return parsed
        }
    } catch {
        // Ignore malformed streamed JSON and fall back to the structured args.
    }

    return isPlainRecord(toolArgs) ? cloneAssistantData(toolArgs) : {}
}

export function resolveAssistantToolExecution(
    toolCallId: string,
    toolName: string,
    toolActivity: AssistantToolExecutionActivityLike[],
): { toolCallId: string; toolName: string } {
    let resolvedToolCallId = toolCallId.trim()
    let resolvedToolName = toolName.trim()

    if (resolvedToolCallId) {
        const matchingActivity = toolActivity.find(activity => activity.id === resolvedToolCallId)
        if (matchingActivity) {
            resolvedToolName ||= matchingActivity.name
        }
    }

    if (!resolvedToolCallId || !resolvedToolName) {
        const runningActivities = toolActivity.filter(activity => activity.status === 'running')
        if (runningActivities.length === 1) {
            resolvedToolCallId ||= runningActivities[0].id
            resolvedToolName ||= runningActivities[0].name
        }
    }

    return {
        toolCallId: resolvedToolCallId,
        toolName: resolvedToolName,
    }
}

function addAlias(aliasMap: Map<string, string>, alias: string | null | undefined, nodeId: string): void {
    if (!alias) {
        return
    }

    const trimmed = alias.trim()
    if (!trimmed) {
        return
    }

    aliasMap.set(trimmed, nodeId)
    aliasMap.set(trimmed.toLowerCase(), nodeId)
}

export function isWorkflowAssistantNodeType(value: string | null | undefined): value is NodeType {
    return typeof value === 'string' && SUPPORTED_NODE_TYPES.has(value as NodeType)
}

export function buildUniqueAssistantNodeId(type: NodeType, reservedIds: Set<string>): string {
    let index = 1
    let candidate = `${type}_${index}`
    while (reservedIds.has(candidate)) {
        index += 1
        candidate = `${type}_${index}`
    }
    return candidate
}

export function normalizeAssistantActions(
    actions: WorkflowAssistantAction[],
    existingNodes: WorkflowNode[],
): WorkflowAssistantAction[] {
    const reservedIds = new Set(existingNodes.map(node => node.id))

    return actions.map(action => {
        if (action.action_type !== 'add_node') {
            return action
        }

        const nodeType = isWorkflowAssistantNodeType(action.node_type) ? action.node_type : 'input'
        const requestedId = action.node_id && !reservedIds.has(action.node_id) ? action.node_id : null
        const nodeId = requestedId ?? buildUniqueAssistantNodeId(nodeType, reservedIds)
        reservedIds.add(nodeId)

        return {
            ...action,
            node_id: nodeId,
            node_type: nodeType,
        }
    })
}

export function createAssistantAliasMap(
    actions: WorkflowAssistantAction[],
    existingNodes: WorkflowNode[],
    runtimeAliases: Map<string, string> = new Map(),
): Map<string, string> {
    const aliasMap = new Map<string, string>()

    for (const node of existingNodes) {
        addAlias(aliasMap, node.id, node.id)
        addAlias(aliasMap, node.name, node.id)
    }

    for (const action of actions) {
        if (action.action_type !== 'add_node' || !action.node_id) {
            continue
        }

        addAlias(aliasMap, action.node_id, action.node_id)
        addAlias(aliasMap, action.node_name, action.node_id)

        const updatedName = action.updates && typeof action.updates.name === 'string'
            ? action.updates.name
            : null
        addAlias(aliasMap, updatedName, action.node_id)
    }

    for (const [alias, nodeId] of runtimeAliases) {
        addAlias(aliasMap, alias, nodeId)
    }

    return aliasMap
}

export function normalizeAssistantNextNodeReferences(
    nextNode: unknown,
    existingNodes: WorkflowNode[],
    aliasMap: Map<string, string>,
): unknown {
    if (typeof nextNode === 'string') {
        return resolveAssistantNodeReference(nextNode, existingNodes, aliasMap) ?? nextNode
    }

    if (Array.isArray(nextNode)) {
        return nextNode.map(entry => {
            if (!isPlainRecord(entry)) {
                return cloneAssistantData(entry)
            }

            const normalized = cloneAssistantData(entry)
            if (typeof normalized.target === 'string') {
                normalized.target = resolveAssistantNodeReference(normalized.target, existingNodes, aliasMap) ?? normalized.target
            }
            return normalized
        })
    }

    if (!isPlainRecord(nextNode)) {
        return cloneAssistantData(nextNode)
    }

    const normalized = cloneAssistantData(nextNode)

    if (typeof normalized.target === 'string') {
        normalized.target = resolveAssistantNodeReference(normalized.target, existingNodes, aliasMap) ?? normalized.target
    }

    if (Array.isArray(normalized.targets)) {
        normalized.targets = normalized.targets.map(target => {
            if (typeof target !== 'string') {
                return target
            }
            return resolveAssistantNodeReference(target, existingNodes, aliasMap) ?? target
        })
    }

    if (typeof normalized.downstream_join_id === 'string') {
        normalized.downstream_join_id = resolveAssistantNodeReference(
            normalized.downstream_join_id,
            existingNodes,
            aliasMap,
        ) ?? normalized.downstream_join_id
    }

    return normalized
}

export function isAssistantJoinNodeReference(
    nodeId: string | null | undefined,
    existingNodes: WorkflowNode[],
): boolean {
    if (!nodeId) {
        return false
    }

    return existingNodes.some(node => node.id === nodeId && node.node_type === 'join')
}

export function assistantTargetsJoinDirectly(
    nextNode: unknown,
    existingNodes: WorkflowNode[],
): boolean {
    if (typeof nextNode === 'string') {
        return isAssistantJoinNodeReference(nextNode, existingNodes)
    }

    if (Array.isArray(nextNode)) {
        return nextNode.some(entry => isPlainRecord(entry)
            && typeof entry.target === 'string'
            && isAssistantJoinNodeReference(entry.target, existingNodes))
    }

    if (!isPlainRecord(nextNode)) {
        return false
    }

    if (typeof nextNode.target === 'string' && isAssistantJoinNodeReference(nextNode.target, existingNodes)) {
        return true
    }

    if (Array.isArray(nextNode.targets)) {
        return nextNode.targets.some(target => typeof target === 'string' && isAssistantJoinNodeReference(target, existingNodes))
    }

    return false
}

export function resolveAssistantNodeReference(
    reference: string | null | undefined,
    existingNodes: WorkflowNode[],
    aliasMap: Map<string, string>,
): string | null {
    if (!reference) {
        return null
    }

    const trimmed = reference.trim()
    if (!trimmed) {
        return null
    }

    const aliasMatch = aliasMap.get(trimmed) ?? aliasMap.get(trimmed.toLowerCase())
    if (aliasMatch) {
        return aliasMatch
    }

    const exactIdMatch = existingNodes.find(node => node.id === trimmed)
    if (exactIdMatch) {
        return exactIdMatch.id
    }

    const exactNameMatch = existingNodes.find(node => node.name === trimmed)
    if (exactNameMatch) {
        return exactNameMatch.id
    }

    const lowerReference = trimmed.toLowerCase()
    const caseInsensitiveNameMatch = existingNodes.find(node => node.name.toLowerCase() === lowerReference)
    if (caseInsensitiveNameMatch) {
        return caseInsensitiveNameMatch.id
    }

    return null
}

export function sortAssistantActionsForApply(actions: WorkflowAssistantAction[]): WorkflowAssistantAction[] {
    return actions
        .map((action, index) => ({ action, index }))
        .sort((left, right) => {
            const priorityDiff = ACTION_PRIORITY[left.action.action_type] - ACTION_PRIORITY[right.action.action_type]
            if (priorityDiff !== 0) {
                return priorityDiff
            }
            return left.index - right.index
        })
        .map(entry => entry.action)
}

/**
 * Keys where the assistant value must always fully overwrite — never deep-merge.
 * `next_node` routing may change type (EdgeConfig → ParallelT → ConditionT[])
 * and a deep merge would produce invalid hybrids.
 */
const OVERWRITE_KEYS: ReadonlySet<string> = new Set(['next_node'])

export function mergeAssistantNodeUpdate<T extends Record<string, unknown>>(
    base: T,
    updates: Record<string, unknown> | null | undefined,
): T {
    if (!updates) {
        return cloneAssistantData(base)
    }

    const merged = cloneAssistantData(base) as Record<string, unknown>

    for (const [key, value] of Object.entries(updates)) {
        if (OVERWRITE_KEYS.has(key)) {
            merged[key] = cloneAssistantData(value)
            continue
        }

        const currentValue = merged[key]

        if (isPlainRecord(currentValue) && isPlainRecord(value)) {
            merged[key] = mergeAssistantNodeUpdate(currentValue, value)
            continue
        }

        merged[key] = cloneAssistantData(value)
    }

    return merged as T
}
